import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { Calendar, Users, ClipboardCheck, Clock, User, UserCheck, MessageCircle } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { usePullToRefresh, RefreshIndicator } from '../../hooks/usePullToRefresh';

const CoachDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamChats, setTeamChats] = useState([]);
  const [selectedClassForEnrollment, setSelectedClassForEnrollment] = useState(null);
  const [enrolledPlayers, setEnrolledPlayers] = useState([]);
  const [loadingEnrolledPlayers, setLoadingEnrolledPlayers] = useState(false);
  
  // Trial players state
  const [trialPlayers, setTrialPlayers] = useState([]);
  const [showAllTrials, setShowAllTrials] = useState(false);
  const [_loadingTrials, setLoadingTrials] = useState(true);
  const [trialNoteDialog, setTrialNoteDialog] = useState(false);
  const [selectedTrial, setSelectedTrial] = useState(null);
  const [trialNote, setTrialNote] = useState({
    content: '',
    parent_recommendation: '',
    admin_note: '',
    is_urgent: false
  });
  const [submittingNote, setSubmittingNote] = useState(false);

  const fetchAllData = async () => {
    try {
      const [statsRes, trialsRes, chatsRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/trial-requests/coach/scheduled-trials').catch(() => ({ data: [] })),
        api.get('/chat/teams').catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setTrialPlayers(trialsRes.data || []);
      setTeamChats(chatsRes.data || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
      setLoadingTrials(false);
    }
  };

  const { refreshing, onTouchStart, onTouchEnd } = usePullToRefresh(fetchAllData);

  useEffect(() => {
    fetchAllData();
  }, []);

  const handleEnrollmentClick = async (classItem) => {
    setSelectedClassForEnrollment(classItem);
    setLoadingEnrolledPlayers(true);
    try {
      const response = await api.get(`/classes/${classItem.id}/enrollments`);
      setEnrolledPlayers(response.data);
    } catch (error) {
      console.error('Error fetching enrolled players:', error);
      setEnrolledPlayers([]);
    } finally {
      setLoadingEnrolledPlayers(false);
    }
  };

  const openTrialNoteDialog = (trial) => {
    setSelectedTrial(trial);
    setTrialNote({
      content: '',
      parent_recommendation: '',
      admin_note: '',
      is_urgent: false
    });
    setTrialNoteDialog(true);
  };

  const handleSubmitTrialNote = async () => {
    if (!trialNote.content.trim()) {
      toast.error('Please enter note content');
      return;
    }

    setSubmittingNote(true);
    try {
      await api.post('/coach-notes', {
        note_type: 'trial_player',
        class_id: selectedTrial.class_id,
        trial_id: selectedTrial.id,
        trial_player_name: selectedTrial.child_name,
        date: new Date().toISOString().split('T')[0],
        content: trialNote.content,
        is_urgent: trialNote.is_urgent,
        parent_recommendation: trialNote.parent_recommendation || null,
        admin_note: trialNote.admin_note || null
      });
      toast.success('Note submitted successfully');
      setTrialNoteDialog(false);
      setSelectedTrial(null);
    } catch (error) {
      console.error('Error submitting note:', error);
      toast.error('Failed to submit note');
    } finally {
      setSubmittingNote(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Coach Dashboard">
      <div className="space-y-6 lg:space-y-8" data-testid="coach-dashboard" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <RefreshIndicator refreshing={refreshing} />
        {/* Trial Players Alert */}
        {trialPlayers.length > 0 && (
          <Card className="border-l-4 border-l-amber-500 bg-amber-50 border-amber-200" data-testid="trial-players-alert">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2 text-amber-800">
                <UserCheck className="h-5 w-5" />
                Upcoming Trial Players ({trialPlayers.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-amber-700 mb-4">
                You have trial players scheduled for your classes. Please provide feedback after their session.
              </p>
              <div className="space-y-3">
                {(showAllTrials ? trialPlayers : trialPlayers.slice(0, 5)).map((trial) => (
                  <div 
                    key={trial.id} 
                    className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200"
                    data-testid={`trial-player-${trial.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 rounded-full">
                        <User className="h-4 w-4 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{trial.child_name}</p>
                        <p className="text-sm text-slate-500">
                          {trial.class_name} • {trial.trial_date || 'Date TBD'}
                        </p>
                        {trial.skill_level && (
                          <Badge className="mt-1 bg-slate-100 text-slate-600 text-xs">
                            {trial.skill_level}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-amber-700 border-amber-300 hover:bg-amber-100"
                      onClick={() => openTrialNoteDialog(trial)}
                      data-testid={`add-trial-note-${trial.id}`}
                    >
                      Add Note
                    </Button>
                  </div>
                ))}
                {trialPlayers.length > 5 && (
                  <Button
                    variant="ghost"
                    className="w-full text-amber-600 hover:text-amber-700 hover:bg-amber-100"
                    onClick={() => setShowAllTrials(!showAllTrials)}
                    data-testid="show-all-trials-btn"
                  >
                    {showAllTrials 
                      ? 'Show less' 
                      : `+${trialPlayers.length - 5} more trial players - Click to show all`}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
          <Card className="border border-slate-200">
            <CardContent className="p-3 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm font-medium text-slate-500">My Classes</p>
                  <p className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 mt-1">
                    {stats?.total_classes || 0}
                  </p>
                </div>
                <div className="p-2 lg:p-3 rounded-lg bg-blue-500">
                  <Calendar className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-slate-200">
            <CardContent className="p-3 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm font-medium text-slate-500">Students</p>
                  <p className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 mt-1">
                    {stats?.total_students || 0}
                  </p>
                </div>
                <div className="p-2 lg:p-3 rounded-lg bg-blue-500">
                  <Users className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-slate-200">
            <CardContent className="p-3 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm font-medium text-slate-500">Hours/Mo</p>
                  <p className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 mt-1">
                    {stats?.hours_this_month?.toFixed(1) || 0}
                  </p>
                  {stats?.pending_hours > 0 && (
                    <p className="text-[10px] lg:text-xs text-amber-600 mt-0.5">{stats.pending_hours.toFixed(1)} pending</p>
                  )}
                </div>
                <div className="p-2 lg:p-3 rounded-lg bg-green-500">
                  <Clock className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-slate-200">
            <CardContent className="p-3 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm font-medium text-slate-500">Attendance</p>
                  <p className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 mt-1">
                    {stats?.today_attendance || 0}
                  </p>
                </div>
                <div className="p-2 lg:p-3 rounded-lg bg-purple-500">
                  <ClipboardCheck className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* My Classes */}
        <Card className="border border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-xl">My Classes</CardTitle>
              <Link 
                to="/coach/attendance" 
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                data-testid="mark-attendance-link"
              >
                Mark Attendance
              </Link>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {!stats?.classes || stats.classes.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                No classes assigned yet
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {stats.classes.map((classItem) => (
                  <div 
                    key={classItem.id} 
                    className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                    data-testid={`class-${classItem.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Clock className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{classItem.name}</p>
                        <p className="text-sm text-slate-500">
                          {classItem.schedule_day} • {classItem.start_time} - {classItem.end_time}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <button
                        onClick={() => handleEnrollmentClick(classItem)}
                        className="font-medium text-slate-900 hover:text-blue-600 hover:underline cursor-pointer"
                        data-testid={`class-enrollment-count-${classItem.id}`}
                      >
                        {classItem.current_enrollment}/{classItem.max_capacity} students
                      </button>
                      <p className="text-sm text-slate-500">{classItem.location || 'No location'}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Team Chats Card */}
        {teamChats.length > 0 && (
          <Card className="border border-slate-200">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <MessageCircle className="h-5 w-5 text-blue-500" />
                Team Chats
                {teamChats.reduce((sum, c) => sum + (c.unread_count || 0), 0) > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                    {teamChats.reduce((sum, c) => sum + (c.unread_count || 0), 0)} new
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {teamChats.slice(0, 4).map((chat) => (
                  <div 
                    key={chat.team_id}
                    className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => navigate('/coach/tournaments')}
                    data-testid={`chat-preview-${chat.team_id}`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <MessageCircle className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900 truncate">{chat.team_name}</p>
                          {chat.unread_count > 0 && (
                            <span className="bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 flex-shrink-0">
                              {chat.unread_count}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 truncate">{chat.tournament_name}</p>
                        {chat.last_message && (
                          <p className="text-sm text-slate-600 truncate mt-1">
                            <span className="font-medium">{chat.last_message.sender_name}:</span>{' '}
                            {chat.last_message.content}
                          </p>
                        )}
                      </div>
                    </div>
                    {chat.last_message && (
                      <span className="text-xs text-slate-400 ml-2 flex-shrink-0">
                        {new Date(chat.last_message.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                ))}
              </div>
              {teamChats.length > 4 && (
                <div className="p-3 border-t border-slate-100 text-center">
                  <Button 
                    variant="link" 
                    className="text-blue-600"
                    onClick={() => navigate('/coach/tournaments')}
                  >
                    View all {teamChats.length} team chats
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:gap-4">
          <Link to="/coach/hours" data-testid="quick-log-hours">
            <Card className="card-hover border border-slate-200 cursor-pointer group">
              <CardContent className="p-4 lg:p-6 flex items-center gap-3 lg:gap-4">
                <div className="p-2 lg:p-3 rounded-lg bg-green-100 group-hover:bg-green-500 transition-colors">
                  <Clock className="h-5 w-5 lg:h-6 lg:w-6 text-green-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 text-sm lg:text-base">Log Hours</p>
                  <p className="text-xs lg:text-sm text-slate-500 hidden sm:block">Submit your working hours</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/coach/attendance" data-testid="quick-mark-attendance">
            <Card className="card-hover border border-slate-200 cursor-pointer group">
              <CardContent className="p-4 lg:p-6 flex items-center gap-3 lg:gap-4">
                <div className="p-2 lg:p-3 rounded-lg bg-purple-100 group-hover:bg-purple-500 transition-colors">
                  <ClipboardCheck className="h-5 w-5 lg:h-6 lg:w-6 text-purple-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 text-sm lg:text-base">Mark Attendance</p>
                  <p className="text-xs lg:text-sm text-slate-500 hidden sm:block">Record student attendance</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/coach/profile" data-testid="quick-my-profile">
            <Card className="card-hover border border-slate-200 cursor-pointer group">
              <CardContent className="p-4 lg:p-6 flex items-center gap-3 lg:gap-4">
                <div className="p-2 lg:p-3 rounded-lg bg-blue-100 group-hover:bg-blue-500 transition-colors">
                  <User className="h-5 w-5 lg:h-6 lg:w-6 text-blue-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 text-sm lg:text-base">My Profile</p>
                  <p className="text-xs lg:text-sm text-slate-500 hidden sm:block">Update your information</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Enrolled Players Dialog */}
        <Dialog 
          open={!!selectedClassForEnrollment} 
          onOpenChange={(open) => !open && setSelectedClassForEnrollment(null)}
        >
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Enrolled Players - {selectedClassForEnrollment?.name}
              </DialogTitle>
            </DialogHeader>
            {loadingEnrolledPlayers ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
              </div>
            ) : enrolledPlayers.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No players enrolled in this class</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Player</TableHead>
                    <TableHead>Parent</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enrolledPlayers.map((enrollment, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <span className="font-medium">{enrollment.player_name}</span>
                      </TableCell>
                      <TableCell>{enrollment.parent_name}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {enrollment.parent_email && (
                            <p className="text-slate-600">{enrollment.parent_email}</p>
                          )}
                          {enrollment.parent_phone && (
                            <p className="text-slate-500">{enrollment.parent_phone}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          className={
                            enrollment.enrollment_status === 'active' || enrollment.enrollment_status === 'current'
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-amber-100 text-amber-700'
                          }
                        >
                          {enrollment.enrollment_status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DialogContent>
        </Dialog>

        {/* Trial Player Note Dialog */}
        <Dialog open={trialNoteDialog} onOpenChange={setTrialNoteDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-amber-600" />
                Trial Player Feedback
              </DialogTitle>
            </DialogHeader>
            
            {selectedTrial && (
              <div className="space-y-4 mt-2">
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="font-medium text-slate-900">{selectedTrial.child_name}</p>
                  <p className="text-sm text-slate-500">
                    {selectedTrial.class_name} • {selectedTrial.trial_date || 'Date TBD'}
                  </p>
                  {selectedTrial.parent_name && (
                    <p className="text-sm text-slate-500 mt-1">
                      Parent: {selectedTrial.parent_name}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Session Notes <span className="text-red-500">*</span></Label>
                  <Textarea
                    value={trialNote.content}
                    onChange={(e) => setTrialNote({...trialNote, content: e.target.value})}
                    placeholder="How was the player's session? Skills observed, areas to improve..."
                    rows={3}
                    data-testid="trial-note-content"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Recommendation for Parent (Optional)</Label>
                  <Textarea
                    value={trialNote.parent_recommendation}
                    onChange={(e) => setTrialNote({...trialNote, parent_recommendation: e.target.value})}
                    placeholder="What would you recommend for this player? Suggested program, practice tips..."
                    rows={2}
                    data-testid="trial-note-parent-rec"
                  />
                  <p className="text-xs text-slate-500">This can be shared with the parent</p>
                </div>

                <div className="space-y-2">
                  <Label>Note for Admin (Optional)</Label>
                  <Textarea
                    value={trialNote.admin_note}
                    onChange={(e) => setTrialNote({...trialNote, admin_note: e.target.value})}
                    placeholder="Any internal notes for admin review..."
                    rows={2}
                    data-testid="trial-note-admin"
                  />
                  <p className="text-xs text-slate-500">This is only visible to admin</p>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_urgent"
                    checked={trialNote.is_urgent}
                    onChange={(e) => setTrialNote({...trialNote, is_urgent: e.target.checked})}
                    className="rounded border-slate-300"
                    data-testid="trial-note-urgent"
                  />
                  <label htmlFor="is_urgent" className="text-sm text-slate-600">
                    Mark as urgent (requires immediate attention)
                  </label>
                </div>
              </div>
            )}

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setTrialNoteDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitTrialNote}
                disabled={submittingNote || !trialNote.content.trim()}
                className="bg-amber-500 hover:bg-amber-600"
                data-testid="submit-trial-note"
              >
                {submittingNote ? 'Submitting...' : 'Submit Feedback'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default CoachDashboard;
